<template>
  <div
    class="dash-stat"
    :class="[
      `dash-stat--${variant}`,
      { 'dash-stat--link': !!url, 'dash-stat--masked': !isVisible },
    ]"
    :style="{ animationDelay: `${(cardIndex % 8) * 40}ms` }"
    @click="openUrl"
  >
    <div class="dash-stat__glow" aria-hidden="true" />

    <div class="dash-stat__head">
      <span class="dash-stat__icon-chip" aria-hidden="true">
        <feather-icon :icon="icon" size="17" />
      </span>
      <div class="dash-stat__head-text">
        <span class="dash-stat__label">{{ $t(title) }}</span>
        <span v-if="!isVisible" class="dash-stat__status">{{
          $t("Hidden")
        }}</span>
      </div>
      <button
        type="button"
        class="dash-stat__eye"
        :class="{ 'dash-stat__eye--off': !isVisible }"
        :title="isVisible ? $t('Hide') : $t('Show')"
        @click.stop="$emit('toggle-visibility')"
      >
        <feather-icon
          :icon="isVisible ? 'EyeIcon' : 'EyeOffIcon'"
          size="15"
        />
      </button>
    </div>

    <div class="dash-stat__metric">
      <transition name="dash-stat-val" mode="out-in">
        <div :key="isVisible ? 'show' : 'hide'" class="dash-stat__metric-inner">
          <span v-if="isVisible && valueHint" class="dash-stat__prefix">{{
            valueHint
          }}</span>
          <span
            class="dash-stat__value"
            :class="{ 'dash-stat__value--masked': !isVisible }"
          >
            {{ displayValue }}
          </span>
        </div>
      </transition>
    </div>

    <div
      class="dash-stat__footer"
      :class="{ 'dash-stat__footer--hint': !isVisible }"
    >
      <template v-if="!isVisible">
        <span class="dash-stat__meta-text">{{ $t("Tap eye to reveal") }}</span>
      </template>
      <template v-else-if="footerLine">
        <span v-if="footerLine.type === 'pill'" class="dash-stat__meta-pill">
          <strong>{{ footerLine.value }}</strong> {{ $t(footerLine.label) }}
        </span>
        <span v-else class="dash-stat__meta-text">{{ footerLine.text }}</span>
      </template>
      <span v-else class="dash-stat__meta-placeholder" aria-hidden="true"
        >&#8203;</span
      >
    </div>
  </div>
</template>

<script>
export default {
  props: {
    title: String,
    value: [Number, String],
    icon: String,
    isVisible: Boolean,
    variant: { type: String, default: "primary" },
    url: { type: String, default: "" },
    otherText: { type: [Number, String], default: "" },
    otherValue: { type: [Number, String], default: "" },
    bottomText: { type: [Number, String], default: "" },
    cardIndex: { type: Number, default: 0 },
  },
  computed: {
    displayValue() {
      return this.isVisible ? this.value : "••••••";
    },
    valueHint() {
      if (!this.isVisible) return "";
      const t = String(this.title || "").toLowerCase();
      if (
        t.includes("collection") ||
        t.includes("dues") ||
        t.includes("estimation")
      ) {
        return "Rs.";
      }
      return "";
    },
    footerLine() {
      if (!this.isVisible) return null;
      if (this.bottomText) {
        return { type: "text", text: this.bottomText };
      }
      const hasOtherLabel =
        this.otherText != null && String(this.otherText).trim() !== "";
      if (!hasOtherLabel) return null;
      const hasValue =
        this.otherValue !== "" &&
        this.otherValue != null &&
        !(typeof this.otherValue === "number" && Number.isNaN(this.otherValue));
      if (hasValue) {
        return {
          type: "pill",
          value: this.otherValue,
          label: this.otherText,
        };
      }
      return { type: "text", text: String(this.otherText) };
    },
  },
  methods: {
    openUrl() {
      if (this.url) window.open(this.url, "_blank");
    },
  },
};
</script>

<style lang="scss" scoped>
.dash-stat {
  --stat-accent: #6366f1;
  --stat-soft: rgba(99, 102, 241, 0.12);
  --stat-soft-2: rgba(99, 102, 241, 0.06);
  --stat-icon-bg: linear-gradient(135deg, #6366f1, #818cf8);
  --stat-icon-color: #4338ca;

  position: relative;
  height: 100%;
  min-height: 7.35rem;
  padding: 0.72rem 0.9rem 0.62rem;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.58);
  backdrop-filter: blur(14px) saturate(1.2);
  -webkit-backdrop-filter: blur(14px) saturate(1.2);
  border: 1px solid rgba(255, 255, 255, 0.75);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.95) inset,
    0 8px 24px rgba(115, 103, 240, 0.07);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 0;
  animation: dashStatIn 0.55s cubic-bezier(0.22, 1, 0.36, 1) both;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    border-color 0.2s ease;

  &--success {
    --stat-accent: #10b981;
    --stat-soft: rgba(16, 185, 129, 0.14);
    --stat-soft-2: rgba(16, 185, 129, 0.07);
    --stat-icon-bg: linear-gradient(135deg, #059669, #34d399);
    --stat-icon-color: #047857;
  }
  &--info {
    --stat-accent: #0ea5e9;
    --stat-soft: rgba(14, 165, 233, 0.14);
    --stat-soft-2: rgba(14, 165, 233, 0.07);
    --stat-icon-bg: linear-gradient(135deg, #0284c7, #38bdf8);
    --stat-icon-color: #0369a1;
  }
  &--primary {
    --stat-accent: #7c3aed;
    --stat-soft: rgba(124, 58, 237, 0.14);
    --stat-soft-2: rgba(124, 58, 237, 0.07);
    --stat-icon-bg: linear-gradient(135deg, #6d28d9, #a78bfa);
    --stat-icon-color: #5b21b6;
  }
  &--warning {
    --stat-accent: #f59e0b;
    --stat-soft: rgba(245, 158, 11, 0.16);
    --stat-soft-2: rgba(245, 158, 11, 0.08);
    --stat-icon-bg: linear-gradient(135deg, #d97706, #fbbf24);
    --stat-icon-color: #b45309;
  }
  &--danger {
    --stat-accent: #ef4444;
    --stat-soft: rgba(239, 68, 68, 0.14);
    --stat-soft-2: rgba(239, 68, 68, 0.07);
    --stat-icon-bg: linear-gradient(135deg, #dc2626, #f87171);
    --stat-icon-color: #b91c1c;
  }

  &--masked {
    background: linear-gradient(165deg, #f8fafc 0%, #fff 100%);
  }

  &--link {
    cursor: pointer;
  }

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(115, 103, 240, 0.22);
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 1) inset,
      0 12px 28px rgba(115, 103, 240, 0.12);
  }

  &__glow {
    position: absolute;
    top: -50%;
    right: -25%;
    width: 55%;
    height: 70%;
    background: radial-gradient(circle, var(--stat-soft), transparent 70%);
    pointer-events: none;
    opacity: 0.85;
  }

  &__head {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    position: relative;
    z-index: 1;
    flex: 0 0 auto;
    min-height: 2.125rem;
    margin-bottom: 0.35rem;
  }

  &__icon-chip {
    width: 2.125rem;
    height: 2.125rem;
    border-radius: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    background: var(--stat-icon-bg);
    box-shadow: 0 4px 12px var(--stat-soft);
    flex-shrink: 0;
  }

  &__head-text {
    flex: 1;
    min-width: 0;
  }

  &__label {
    display: block;
    font-size: 0.75rem;
    font-weight: 600;
    color: #64748b;
    line-height: 1.25;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__status {
    display: none;
  }

  &__eye {
    width: 1.75rem;
    height: 1.75rem;
    border: none;
    border-radius: 8px;
    background: var(--stat-soft-2);
    color: var(--stat-icon-color);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.2s ease, transform 0.15s ease;
    flex-shrink: 0;

    &--off {
      opacity: 0.85;
    }

    &:hover {
      background: var(--stat-soft);
      transform: scale(1.05);
    }
  }

  &__metric {
    flex: 1 1 auto;
    display: flex;
    align-items: flex-end;
    position: relative;
    z-index: 1;
    min-height: 2.35rem;
    padding-bottom: 0.4rem;
  }

  &__metric-inner {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0.2rem 0.35rem;
    width: 100%;
    padding-left: 0.05rem;
  }

  &__prefix {
    font-size: 0.6875rem;
    font-weight: 600;
    color: #94a3b8;
  }

  &__value {
    font-size: clamp(1.125rem, 1.6vw, 1.375rem);
    font-weight: 700;
    letter-spacing: -0.025em;
    line-height: 1.15;
    font-variant-numeric: tabular-nums;
    color: #2f2b3d;

    &--masked {
      font-size: 1rem;
      letter-spacing: 0.06em;
      color: #cbd5e1;
    }
  }

  &__footer {
    flex: 0 0 auto;
    min-height: 2.05rem;
    display: flex;
    align-items: center;
    position: relative;
    z-index: 1;
    padding-top: 0.45rem;
    border-top: 1px solid rgba(241, 245, 249, 0.95);

    &--hint {
      border-top-color: rgba(241, 245, 249, 0.65);
    }
  }

  &__meta-placeholder {
    display: block;
    width: 100%;
    font-size: 0.6875rem;
    line-height: 1.35;
    min-height: 1.35em;
    visibility: hidden;
    user-select: none;
  }

  &__meta-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.2rem;
    padding: 0.15rem 0.45rem;
    border-radius: 999px;
    font-size: 0.6875rem;
    font-weight: 600;
    color: #64748b;
    background: #f8fafc;
  }

  &__meta-text {
    font-size: 0.6875rem;
    font-weight: 500;
    color: #64748b;
    line-height: 1.35;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    width: 100%;
  }
}

.dash-stat-val-enter-active,
.dash-stat-val-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.dash-stat-val-enter,
.dash-stat-val-leave-to {
  opacity: 0;
  transform: translateY(4px);
}

@keyframes dashStatIn {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
